/*
This is a Google Autocomplete provider that can be used in allmost all cases. Just
remember that if the client need to filter the results by polygon you CANT use this provider.
*/
import jQuery from 'jquery';
import _ from 'underscore';
import AutocompleteProviderBase from './AutocompleteProviderBase';
import GPlacesHelper from 'helpers/GPlacesHelper';
import GoogleMapsUtils from 'helpers/GoogleMapsUtils';

export default class GoogleAutocomplete extends AutocompleteProviderBase {
  /**
   * Create a GoogleAutocomplete instance 
   * @class GoogleAutocomplete
   * @classdesc This is the Google autocomplete provider
   * @memberof core.components.autocomplete.providers
   * @extends core.components.autocomplete.providers.AutocompleteProviderBase
   * @constructs
   * @param {object} conf - The conf provided by json
   * @param {Widget} widget - The widget where the Autocomplete will be built
   * @example <caption>GoogleAutocomplete conf</caption>
         "icons": ["transit_station", "bus_station", "train_station"],
      "minLength": 3,
      "maxResults": 5,
      "serviceOptions": {
        "componentRestrictions": {
          "ref:country:countryRestrictionAutocomplete": "gb"
        }
      }            
   */
  constructor(conf, widget) {
    super(conf, widget);
    this.map = widget.map;
    this.maxResults = conf.maxResults;
    this.prefixTextSearch = conf.prefixTextSearch || '';
    this._serviceOpt = conf.serviceOptions || {};
    if (this._serviceOpt.bounds) {
      this._serviceOpt.bounds = GoogleMapsUtils.getBounds(this._serviceOpt.bounds);
    }
    this.service = new google.maps.places.AutocompleteService();
  }

  _createCoordinatesHandler(map) {
    return function() {
      let _deferred = jQuery.Deferred();
      GPlacesHelper.getDetails(map, this.code)
        .done(function(place) {
          let location = null;
          if (!_.isUndefined(place[0].geometry.viewport)) {
            location = place[0].geometry;
          } else {
            location = place[0].geometry.location;
          }
          _deferred.resolve(location);
        }).fail(function() {
          _deferred.reject(status);
        });
      return _deferred.promise();
    };
  }

  /**
   * Method to search and normalize the source predictions using Google Places autocomplete service
   * @memberOf GoogleAutocomplete
   * @param {string} text - The text to search in source
   * @param {object} conf - The config provided by {@link GoogleAutocompleteAndPlaces}
   * @returns {promise}
   */
  getPredictions(text, conf) {
    if (typeof text !== 'string') {
      throw new Error('The text is missing');
    }
    this.maxResults = this.maxResults ? this.maxResults : conf.maxResults ? conf.maxResults : 5;
    let deferred = jQuery.Deferred(),
      map = this.map,
      callback = (results, status) => {
        this.requestIsFinished = true;
        let placeID = 'place_id';
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          let normalizeResults = [];
          for (let i = 0; i < results.length; i++) {
            let result = results[i];
            if (result[placeID]) {
              let normalizeObject = {
                code: result[placeID],
                value: result.description,
                coordinates: this._createCoordinatesHandler(map),
                originData: result,
                icon: this.getIconClass(result),
                provider: GoogleAutocomplete.name
              };
              if (this.conf.category) {
                normalizeObject.category = this.conf.category;
              }
              normalizeResults.push(normalizeObject);
            }
          }
          if (normalizeResults.length > this.maxResults) {
            normalizeResults = normalizeResults.slice(0, this.maxResults);
          }
          deferred.resolve(normalizeResults);
        } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          deferred.resolve([]);
        } else {
          deferred.resolve([]);
        }
      };

    if (text.length < this.conf.minLength) {
      this.requestIsFinished = true;
      deferred.resolve([]);
    } else {
      this.getPredictionsFunction(this.getServiceConf(text, this.conf), callback, deferred);
    }
    return deferred.promise();
  }

  /**
   * Set the method to search 
   * @memberOf GoogleAutocomplete
   * @param {AutocompletionRequest} config - The configuration of search
   * @param {function} callback - The callback after the search
   * @returns {function} the method to search
   */
  getPredictionsFunction(config, callback, deferred) {
    return this.service.getPlacePredictions(config, callback);
  }

  /**
   * Set an icon in function of the result
   * @memberOf GoogleAutocomplete
   * @param {object} result - The result of search
   * @returns {string} the class icon
   */
  getIconClass(result) {
    let availableClass = this.conf.icons;
    if (_.isUndefined(availableClass)) {
      return 'default';
    } else if (availableClass === false) {
      return false;
    } else {
      let iconClass = 'default';
      if (result.types) {
        for (let i = 0; i < availableClass.length; i++) {
          if (result.types.indexOf(availableClass[i]) !== -1) {
            iconClass = availableClass[i];
            break;
          }
        }
      }
      return iconClass;
    }
  }

  /**
   * Set the needed config for the Google autocomplete service. This adds to config the
   * input, location and radius to search
   * @memberOf GoogleAutocomplete
   * @param {object} result - The result of search
   * @param {object} conf - The conf to do the search
   * @returns {object} the conf object
   */
  getServiceConf(text, conf = {}) {
    return _.extend({
      input: this.prefixTextSearch + text,
      location: this.map ? this.map.getCenter() : new google.maps.LatLng(this.conf.defaultLocation[0], this.conf.defaultLocation[
        1]),
      radius: 50000
    }, this._serviceOpt, conf);
  }
}



// WEBPACK FOOTER //
// ./lmm-widget/src/components/autocomplete/providers/GoogleAutocomplete.js